import {
  Arrange,
  Checkbox,
  Container,
  Dropdown,
  FormField,
  Icon,
  IconButton,
  Pill,
  SvgCheck,
  SvgLink,
  SvgMoreHoriz,
  SvgPinFill,
  Toast,
  Tooltip,
} from '@loomhq/lens';
import React, { MutableRefObject, useEffect, useState } from 'react';

import { LOOM_URI } from 'constants/routes';
import { CARD_CAPABILITIES, PROFILE_CONTEXT } from 'constants/videoCard';
import { ContextType } from 'utilities/menuOptions/types';
import { secondsToHumanReadableString } from 'utilities/time';
import { getDuration, postSessionTranscodedURL } from 'utilities/video';

import useBulkMenuOptions from 'hooks/useBulkMenuOptions';
import useClipboard from 'hooks/useClipboard';

import PinIcon from './PinIcon';
import VideoThumbnail from '../thumbnail';
import { VideoCardVideo } from './types';

import styles from './styles.module.less';

export default function Thumbnail({
  video,
  context,
  isChecked,
  linkRef,
  sort,
  currentPinMaxValue,
  onCheck,
}: {
  video: VideoCardVideo;
  context: ContextType;
  isChecked: boolean;
  linkRef: MutableRefObject<HTMLAnchorElement | null>;
  sort: number;
  currentPinMaxValue: number;
  onCheck: (isChecked, isMultiSelect) => void;
}): JSX.Element {
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [toastMessage, setToastMessage] = useState(false);

  useEffect(() => {
    async function getDownloadUrl() {
      const downloadUrl = await new Promise(resolve => {
        postSessionTranscodedURL(video.id, null, mp4URL => {
          resolve(mp4URL);
        });
      });

      setDownloadUrl(downloadUrl ? String(downloadUrl) : undefined);
    }

    if (!downloadUrl) {
      getDownloadUrl();
    }
  }, [video.id, downloadUrl]);

  const menuOptions = useBulkMenuOptions([video], {
    type: context,
    downloadUrl,
  });

  const dropdownOptions = menuOptions.map(option => ({
    title: option.label,
    onClick: option.onClick,
  }));

  // Doesn't stay open when hovered off
  const handleButtonFocus = () => {
    linkRef.current?.classList.add(styles.showHoverState);
  };
  const handleButtonBlur = () => {
    linkRef.current?.classList.remove(styles.showHoverState);
  };

  // Actions stay open when hovered off
  const handleActionFocus = () => {
    linkRef.current?.classList.add(styles.stayOpen);
  };
  const handleActionBlur = () => {
    linkRef.current?.classList.remove(styles.stayOpen);
  };

  const videoUrl = `${LOOM_URI}/share/${video?.id}`;
  const [isCopied, setCopied] = useClipboard(videoUrl, {
    successDuration: 2000,
  });
  const copyLink = () => setCopied();
  const copyBtnText = isCopied ? 'Link copied!' : 'Copy link';

  const duration = secondsToHumanReadableString(getDuration(video));
  const viewed = video.currentUserHasWatched;

  const canPin =
    CARD_CAPABILITIES['hasPinIcon'].includes(context) &&
    video.current_user_is_owner;

  return (
    <div className={styles.videoCardThumbnailWrapper}>
      <VideoThumbnail video={video} />

      <div className={styles.videoCardHoverShow}>
        {CARD_CAPABILITIES['hasBulkActions'].includes(context) && (
          <div className="theme-dark">
            <Container
              position="absolute"
              top="medium"
              left="medium"
              zIndex={2}
            >
              <FormField
                // @ts-expect-error label allows JSX Elements
                label={<span className="srOnly">Add for bulk actions</span>}
                labelFor={`bulk-action-${video.id}`}
              >
                <Checkbox
                  id={`bulk-action-${video.id}`}
                  isChecked={isChecked}
                  onChange={e =>
                    onCheck(e.target.checked, e.nativeEvent.shiftKey)
                  }
                />
              </FormField>
            </Container>
          </div>
        )}

        <Container position="absolute" top="medium" right="medium">
          <Arrange gap="small" autoFlow="row">
            {CARD_CAPABILITIES['hasDropdownActions'].includes(context) && (
              <Dropdown
                menuPosition="right"
                // @ts-expect-error options allow JSX Elements
                options={dropdownOptions}
                trigger={
                  <div className={styles.videoCardButton}>
                    <Tooltip content="More actions..." placement="leftCenter">
                      <IconButton
                        altText="More"
                        icon={<SvgMoreHoriz />}
                        size="small"
                        onFocus={handleActionFocus}
                        onBlur={handleActionBlur}
                      />
                    </Tooltip>
                  </div>
                }
                triggerOffset={6}
              />
            )}
            <div className={styles.videoCardButton}>
              <Tooltip content={copyBtnText} placement="leftCenter">
                <IconButton
                  altText={`Copy link to ${video.name}`}
                  icon={<SvgLink />}
                  size="small"
                  onClick={() => {
                    copyLink();
                  }}
                  onFocus={handleButtonFocus}
                  onBlur={handleButtonBlur}
                />
              </Tooltip>
            </div>
            {canPin && (
              <div className={styles.videoCardButton}>
                <PinIcon
                  video={video}
                  linkRef={linkRef}
                  sort={sort}
                  currentPinMaxValue={currentPinMaxValue}
                  setToastMessage={setToastMessage}
                />
              </div>
            )}
          </Arrange>
        </Container>
      </div>

      {context === PROFILE_CONTEXT && sort > 0 && (
        <div className={styles.videoCardHoverHide}>
          <div className="theme-dark">
            <Container
              backgroundColor="background"
              radius="medium"
              padding="xsmall"
              position="absolute"
              right="small"
              top="small"
            >
              <Icon color="white" icon={<SvgPinFill />} size={2} />
            </Container>
          </div>
        </div>
      )}

      {viewed && (
        <Container position="absolute" left="small" bottom="small">
          <Pill
            color="white"
            backgroundColor="grey8"
            className={styles.checkmark}
          >
            <Icon color="white" size={2} icon={<SvgCheck />} />
            Viewed
          </Pill>
        </Container>
      )}

      {duration && (
        <Container position="absolute" right="small" bottom="small">
          <Pill color="white" backgroundColor="grey8">
            {duration}
          </Pill>
        </Container>
      )}

      <Toast isOpen={toastMessage} onCloseClick={() => setToastMessage(false)}>
        Video pinned to top of profile!
      </Toast>
    </div>
  );
}
